<template>
  <div id="auth-bg" style="background-image: url(static/img/background/background-2.jpg)">
    <div id="login-container">
      <div class="block animation-fadeInQuickInv">
        <!-- Header with logo -->
        <div class="login-header">
          <img
            src="/static/img/background/actslogo.png"
            alt="FOCUS Logo"
            class="login-logo"
          >
        </div>
        <!-- END Header -->
        <div class="login-body">
          <div class="block-title">
            <h2>Sign in</h2>
          </div>

          <!-- Error messages -->
          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <!-- Success message -->
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <!-- Magic Link Form -->
          <form @submit.prevent="handleMagicLink" class="magic-link-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                v-model="email"
                class="form-control"
                required
                :disabled="loading || !!successMessage"
                autofocus
              >
            </div>
            <div class="form-group form-actions">
              <button
                type="submit"
                class="btn btn-block btn-continue"
                :disabled="loading || !!successMessage"
              >
                {{ loading ? 'Please wait...' : 'Continue' }}
              </button>
            </div>
          </form>

          <div class="custom-divider" />

          <div class="social-divider">
            Or sign in with
          </div>

          <div class="button-group">
            <button
              @click="handleMicrosoftLogin"
              class="btn btn-microsoft btn-block social-login-btn"
              type="button"
              :disabled="loading"
            >
              <img src="/static/img/microsoft.png" alt="Microsoft" class="microsoft-icon" /> Microsoft (Staff Only)
            </button>
            <p class="text-center text-muted disclaimer">
              <em>Students: Use the magic link option above. Staff: Sign in with Microsoft.</em>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authRepository from '../repositories/authRepository';

export default {
    name: 'LoginNew',
    data() {
        return {
            email: '',
            loading: false,
            errorMessage: '',
            successMessage: ''
        };
    },
    mounted() {
    // Check for error message in URL query params
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        if (error) {
            this.errorMessage = decodeURIComponent(error);
            // Clean up URL without reloading
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    },
    methods: {
        async handleMagicLink() {
            this.errorMessage = '';
            this.successMessage = '';
            this.loading = true;

            try {
                // Check if email is @focus.org - staff must use Microsoft, redirect with NO login_hint
                if (this.email.toLowerCase().endsWith('@focus.org')) {
                    this.loading = false;
                    await this.handleMicrosoftLogin(); // No loginHint param
                    return;
                }

                // Send magic link for students
                await authRepository.sendMagicLink(this.email);
                this.successMessage = 'Check your email!';
            } catch (error) {
                // eslint-disable-next-line max-len
                this.errorMessage = (error.response && error.response.data && error.response.data.message) || 'An error occurred. Please try again.';
            } finally {
                this.loading = false;
            }
        },
        async handleMicrosoftLogin() {
            // Generate PKCE challenge
            const codeVerifier = this.generateCodeVerifier();
            const codeChallenge = await this.generateCodeChallenge(codeVerifier);

            // Store code verifier for later use in callback
            sessionStorage.setItem('pkce_code_verifier', codeVerifier);

            // Generate a random state for CSRF protection
            const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            const nonce = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

            // Store state in sessionStorage so we can verify it on callback
            sessionStorage.setItem('oidc_state', state);
            sessionStorage.setItem('oidc_nonce', nonce);

            // Build the authorization URL with PKCE
            const tenantId = process.env.VUE_APP_AZURE_TENANT_ID;
            const clientId = process.env.VUE_APP_AZURE_CLIENT_ID;
            const redirectUri = encodeURIComponent(process.env.VUE_APP_AZURE_REDIRECT_URI || 'http://localhost:8080/callback');
            const scope = encodeURIComponent('openid profile email');

            const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?`
        + `client_id=${clientId}`
        + '&response_type=code'
        + `&redirect_uri=${redirectUri}`
        + '&response_mode=query'
        + `&scope=${scope}`
        + `&state=${state}`
        + `&nonce=${nonce}`
        + `&code_challenge=${codeChallenge}`
        + '&code_challenge_method=S256';

            window.location.href = authUrl;
        },

        // Generate a random code verifier for PKCE
        generateCodeVerifier() {
            const array = new Uint8Array(32);
            crypto.getRandomValues(array);
            return this.base64UrlEncode(array);
        },

        // Generate code challenge from verifier
        async generateCodeChallenge(verifier) {
            const encoder = new TextEncoder();
            const data = encoder.encode(verifier);
            const hash = await crypto.subtle.digest('SHA-256', data);
            return this.base64UrlEncode(new Uint8Array(hash));
        },

        // Base64 URL encode
        base64UrlEncode(array) {
            const base64 = btoa(String.fromCharCode.apply(null, array));
            return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        }
    }
};
</script>

<style scoped>
body,
html {
  height: 100%;
  margin: 0;
  padding: 0;
}

#auth-bg {
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  background-size: contain;
  background-position: right center;
  background-repeat: no-repeat;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

#login-container {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  border-radius: 10px;
}

.block {
  padding: 0;
  overflow: hidden;
  background: #fff;
  border-radius: 10px;
}

.animation-fadeInQuickInv {
  animation: fadeInQuick 0.3s ease-in;
}

@keyframes fadeInQuick {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  background: #0c3c61;
  padding: 28px 0 18px 0;
  text-align: center;
  border-radius: 10px 10px 0 0;
}

.login-logo {
  height: 48px;
  width: auto;
  filter: brightness(0) invert(1);
}

.login-body {
  padding: 32px 32px 24px 32px;
  background: #fff;
  border-radius: 0 0 10px 10px;
}

.block-title {
  margin-bottom: 18px;
}

.block-title h2 {
  text-align: center;
  font-weight: 500;
  font-size: 1.5rem;
  color: #222;
  margin: 0;
}

.alert {
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 1rem;
  text-align: center;
}

.alert-danger {
  color: #721c24;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  font-weight: 500;
}

.alert-success {
  color: #468847;
  background: #dff0d8;
  border: 1px solid #d6e9c6;
}

.magic-link-form {
  margin-bottom: 18px;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  font-weight: 500;
  display: block;
  margin-bottom: 0.5rem;
}

.form-control {
  width: 100%;
  padding: 0.6rem 0.75rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 5px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #23486b;
  box-shadow: 0 0 0 0.2rem rgba(35, 72, 107, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.form-actions {
  margin-top: 18px;
}

.btn {
  display: inline-block;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border: 1px solid transparent;
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  border-radius: 8px;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
  text-decoration: none;
  box-sizing: border-box;
  text-transform: none;
}

.btn-block {
  display: block;
  width: 100%;
}

.btn:hover {
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-continue {
  background: #0c3c61;
  border: none;
  color: #fff;
}

.btn-continue:hover:not(:disabled) {
  background: #1a3650;
}

.social-divider {
  text-align: center;
  font-weight: 500;
  margin-bottom: 10px;
}

.custom-divider {
  height: 1px;
  background: #eee;
  margin: 1rem 0;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.social-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.social-login-btn .icon-class {
  margin-right: 8px;
}

.microsoft-icon {
  height: 1em;
  width: auto;
  vertical-align: middle;
  margin-right: 8px;
}

.btn-danger {
  background: #db512b;
  border: none;
}

.btn-danger:hover {
  background: #c84422;
}

.btn-primary {
  background: #3b5998;
  border: none;
}

.btn-primary:hover {
  background: #2d4373;
}

.btn-dark {
  background: #000;
  border: none;
}

.btn-dark:hover {
  background: #333;
}

.btn-microsoft {
  background: #fff;
  border: 1px solid #8c8c8c;
  color: #5e5e5e;
}

.btn-microsoft:hover {
  background: #f5f5f5;
}

.disclaimer {
  font-size: 0.8em;
  margin-top: 0.5rem;
  margin-bottom: 0;
}

@media (max-width: 600px) {
  #login-container {
    max-width: 100vw;
    min-height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }

  .login-body {
    padding: 24px 16px 16px 16px;
  }
}
</style>
